import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { Button, Stack, Tooltip, Typography } from '@mui/material';

import SurchargeDates from '~/components/Vendors/Properties/SurchargeDates';
import { SalesforceSurchargesModal } from '~/components/Vendors/SalesforceSurcharges/SalesforceSurchargesModal';

import { useGetFxRates } from '~/hooks/apis/fx/useGetFxRates';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { roomRateAvailabilityQueryKeys } from '~/hooks/apis/reservation/roomRateAvailabilityQueryKeys';
import { useGetRoomRateSurchargeDates } from '~/hooks/apis/reservation/useGetRoomRateSurchargeDates';
import { useGetSurchargeDateBlockSchema } from '~/hooks/apis/reservation/useGetSurchargeDateBlockSchema';
import { useInvalidateAllRoomRateAvailability } from '~/hooks/apis/reservation/useInvalidateAllRoomRateAvailability';

import ReservationService from '~/services/ReservationService';

import presentFXRates from '~/utils/presentFXRates';

const SURCHARGE_TYPE = 'vendor';

interface Props {
  propertyId: string;
  roomType: App.RoomType;
  roomRate: App.RoomRate;
  propertyRoomTypes: Array<App.RoomType>;
  vendorCurrencyCode: string;
}

export default function SurchargesTab({
  propertyId,
  roomType,
  roomRate,
  propertyRoomTypes,
  vendorCurrencyCode,
}: Props) {
  const queryClient = useQueryClient();
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { data: offer } = useGetOffer(offerId);
  const [isAddSurchargesModalOpen, setIsAddSurchargesModalOpen] = useState(false);
  const { data: surchargeDates = [], refetch } = useGetRoomRateSurchargeDates(propertyId, roomType.id, roomRate.id);
  const { data: surchargeSchema } = useGetSurchargeDateBlockSchema(propertyId, roomType.id, roomRate.id);
  const { data: fxRates } = useGetFxRates();
  const invalidateAll = useInvalidateAllRoomRateAvailability();

  function invalidate() {
    queryClient.invalidateQueries({
      queryKey: roomRateAvailabilityQueryKeys.surcharge(propertyId, roomType.id, roomRate.id),
    });
  }

  const isLimitedTimeExclusive = roomRate.rate_plan?.product_type === 'limited_time_exclusive';
  const canAddSurcharges = !!offer && !!offerId && !!offer.vendor_account_id && isLimitedTimeExclusive;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Surcharge sell prices by date range for this room rate.
        </Typography>
        <Tooltip title={isLimitedTimeExclusive ? '' : 'Add surcharges only supports Limited Time Exclusive rate plans'}>
          <span>
            <Button variant="contained" disabled={!canAddSurcharges} onClick={() => setIsAddSurchargesModalOpen(true)}>
              Add Salesforce surcharges
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {isAddSurchargesModalOpen && canAddSurcharges && (
        <SalesforceSurchargesModal
          closeModal={() => {
            setIsAddSurchargesModalOpen(false);
            refetch();
          }}
          initialOfferId={offerId}
          isOpen
          offers={[offer]}
          propertyId={propertyId}
          roomTypes={[{ ...roomType, room_rates: [roomRate] }]}
          vendorId={offer.vendor_account_id}
          lockedRoomRates={[{ roomType, roomRate }]}
        />
      )}

      <SurchargeDates
        propertyId={propertyId}
        roomType={roomType}
        roomRate={roomRate}
        propertyRoomTypes={propertyRoomTypes}
        vendorCurrencyCode={vendorCurrencyCode}
        surchargeDates={surchargeDates}
        surchargeType={SURCHARGE_TYPE}
        deleteSurchargeDateBlock={async (id: string) => {
          await ReservationService.deleteSurchargeDateBlock(id, propertyId, roomType.id, roomRate.id);
          refetch();
        }}
        deleteSurchargeDateBlockByRoomRateId={async () => {
          await ReservationService.deleteSurchargeDateBlockByRoomRateId(propertyId, roomRate.id, SURCHARGE_TYPE);
          invalidate();
        }}
        deleteSurchargeDateBlockByRatePlanId={async (ratePlanId: string) => {
          await ReservationService.deleteSurchargeDateBlockByRatePlanId(ratePlanId, SURCHARGE_TYPE);
          invalidateAll();
        }}
        onUpdateSurchargeDates={refetch}
        surchargeSchema={surchargeSchema}
        foreignExchangeRates={fxRates ? presentFXRates(fxRates) : {}}
      />
    </>
  );
}
